/**
 * Pure copy helpers for the Scheduled Scans table cells.
 *
 * Kept side-effect free and out of the component (the `deriveProjectSlug`
 * precedent, `lib/projectSlug.ts`) so every branch of the phrasing is testable
 * without mounting a table, and so a second surface reading the same rows
 * cannot describe them differently.
 *
 * Spec: docs/superpowers/specs/2026-08-05-scheduled-scans-design.md (Dashboard UI).
 */
import type { CadenceValue, ScanTypeValue, ScheduleStatus } from '../../lib/api/scheduledScans';

/**
 * The dashboard's joiner glyph, U+00B7 MIDDLE DOT. Read from the one place
 * that renders it in shipped copy, `BillingPage.tsx:114` ("Free trial · N days
 * left"), rather than typed from memory. Not an em dash, which the house copy
 * rules ban outright.
 */
const JOINER = ' · ';

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

/** Two days is where an hour count stops being the useful unit. */
const HOURS_CUTOFF_MS = 48 * HOUR_MS;

/**
 * How late a run may be before the cell stops calling it imminent.
 *
 * The cron ticks hourly and its per-tick budget can defer a row, so being a
 * tick or two late is ordinary operation, not a fault. Two hours is the point
 * where "within the hour" has been wrong twice in a row.
 */
const OVERDUE_GRACE_MS = 2 * HOUR_MS;

export const SCAN_TYPE_LABEL: Record<ScanTypeValue, string> = {
  accessibility: 'Accessibility',
  contrast: 'Contrast',
  tokens: 'Design Tokens',
  // The extension's shipped WORKFLOW_CARD_LABELS['auto-review'] verbatim: one
  // feature, one name, whichever surface you meet it on.
  auto_review: 'Auto Review',
};

export const CADENCE_LABEL: Record<CadenceValue, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
};

/**
 * `paused`, `paused_unreachable` and `paused_bot_protection` are the same
 * state to the scheduler and three different facts to the user: one they
 * chose, one the site caused, one the site's bot protection caused
 * (2026-09-22: a challenge page answered instead of the site; the site is up
 * and "unreachable" would be refuted by opening it). All offer Resume, so
 * the label is the only thing carrying the reason.
 */
export function statusLabel(status: ScheduleStatus): string {
  switch (status) {
    case 'active':
      return 'Active';
    case 'paused':
      return 'Paused';
    case 'paused_unreachable':
      return 'Paused - site unreachable';
    case 'paused_bot_protection':
      return 'Paused - bot protection blocked the scan';
    case 'paused_page_target':
      return 'Paused - check the page URL';
    case 'paused_snapshot_limit':
      return 'Paused - saved page exceeds review size limit';
  }
}

/**
 * When the next run lands, in the coarsest unit that is still honest.
 *
 * The cron ticks hourly, so a run just come due or under an hour out is
 * "within the hour" rather than a countdown we cannot keep. A null
 * `nextRunAt` (paused, or opted out) is stated plainly instead of rendered as
 * a bare dash the user has to interpret.
 *
 * DELIBERATE DIVERGENCE from the implementation plan, which specified a bare
 * "-" placeholder here. A dash in a Next-scan cell is ambiguous (unknown?
 * never? zero?) and this is the ordinary paused-row rendering, not an edge
 * case. Decided during the chunk-5 review; do not "restore" the dash.
 *
 * A LATE run says so. This branch used to be `diff <= HOUR_MS`, which a
 * negative diff also satisfies, so every overdue row read "within the hour"
 * however long it had been stuck. That is a claim no observation can falsify,
 * and it hid a real outage: on prod 2026-08-16, https://overlayqa.com had
 * rendered "Active / Not scanned yet / within the hour" for 41 days while its
 * scan was being skipped entirely. Past OVERDUE_GRACE_MS the cell reports the
 * lateness it can actually measure, in the same units the forward direction
 * uses, so a two-hour backlog and a six-week stall stop looking identical.
 */
export function nextRunLabel(nextRunAt: string | null, now: Date): string {
  if (!nextRunAt) return 'Not scheduled';

  const target = new Date(nextRunAt).getTime();
  // A malformed timestamp must never reach the cell as "in NaN days".
  if (Number.isNaN(target)) return 'Not scheduled';

  const late = latenessMs(nextRunAt, now);
  if (late !== null) {
    return late < HOURS_CUTOFF_MS
      ? `overdue by ${Math.round(late / HOUR_MS)} hrs`
      : `overdue by ${Math.round(late / DAY_MS)} days`;
  }

  const diff = target - now.getTime();
  if (diff <= HOUR_MS) return 'within the hour';
  if (diff < HOURS_CUTOFF_MS) return `in ${Math.round(diff / HOUR_MS)} hrs`;
  return `in ${Math.round(diff / DAY_MS)} days`;
}

/**
 * How late this run is, past the grace window, or null when it is not late.
 *
 * THE single definition of "overdue". `nextRunLabel` and `isRunOverdue` both
 * read it rather than each testing the threshold themselves: two copies would
 * drift the first time either is tuned, and the visible symptom would be a row
 * that reads "overdue by 3 hrs" in calm grey, or "in 7 days" painted as a
 * warning. A swept property test in scheduleRowCopy.test.ts holds the two
 * outputs to each other across the whole range.
 */
function latenessMs(nextRunAt: string | null, now: Date): number | null {
  if (!nextRunAt) return null;
  const target = new Date(nextRunAt).getTime();
  if (Number.isNaN(target)) return null;
  const diff = target - now.getTime();
  return diff < -OVERDUE_GRACE_MS ? -diff : null;
}

/**
 * Is this run late enough to deserve the Next-scan cell's warning treatment?
 *
 * Colour REINFORCES the sentence, it never carries the meaning on its own: the
 * cell already reads "overdue by 41 days" in words, so a user who cannot see
 * the colour loses nothing (WCAG 1.4.1). What the colour buys is that a stuck
 * schedule stops looking identical to a healthy one at a glance, which is the
 * gap that let a 41-day outage sit unnoticed on a page someone had open.
 */
export function isRunOverdue(nextRunAt: string | null, now: Date): boolean {
  return latenessMs(nextRunAt, now) !== null;
}

/** The subset of a schedule row the last-scan cell reads. */
export interface LastScanFields {
  lastScannedAt: string | null;
  lastScore: number | null;
  lastDeltaAdded: number | null;
  lastFindingCount: number;
}

/**
 * What the last run found.
 *
 * `lastDeltaAdded === null` marks a BASELINE run (the first scan of this URL),
 * which has nothing to compare against. Reporting "no change" there would be a
 * lie, so a baseline reports its finding count and only later runs report a
 * delta. The score is dropped rather than rendered as "null" when an engine
 * did not produce one.
 */
export function lastScanLabel(row: LastScanFields): string {
  if (!row.lastScannedAt) return 'Not scanned yet';

  const summary =
    row.lastDeltaAdded === null
      ? `${row.lastFindingCount} finding${row.lastFindingCount === 1 ? '' : 's'}`
      : row.lastDeltaAdded === 0
        ? 'no change'
        : `${row.lastDeltaAdded} new`;

  // "Score" is not decoration. Measured live 2026-08-17, a real run rendered
  // `0 · 8 findings`, and a bare leading number cannot be told apart from a
  // count, a percentage, or a failure value — least of all when it IS zero,
  // which is exactly what a broken number looks like. The finding count already
  // carries its own noun; the score carried none.
  return row.lastScore === null ? summary : `Score ${row.lastScore}${JOINER}${summary}`;
}
